/**
 * TiempoServicioPanel.js
 *
 * Panel flotante para asignar "Tiempos de Servicio" a productos de una orden.
 * Completamente aditivo — no modifica flujos existentes.
 *
 * Flujo:
 *  1. Usuario abre el panel (botón "Tiempo" en la sección Ordenes).
 *  2. Selecciona uno o más productos de la lista.
 *  3. Presiona Tiempo 1 / 2 / 3 / 4.
 *  4. Se llama onAssignTiempo(tiempoLabel, indicesSeleccionados).
 */

import React, { useState } from "react";
import "../styles/TiempoServicioPanel.css";

/**
 * @param {Object}   props
 * @param {boolean}  props.show               - Si el panel está visible.
 * @param {Function} props.onClose            - Cerrar sin aplicar.
 * @param {Array}    props.order              - Array de productos en la orden.
 * @param {Array}    props.tiempos            - Array { Tiempo: "Tiempo 1" } desde config.
 * @param {Function} props.onAssignTiempo     - (tiempoLabel, [indices]) → void.
 * @param {number}   props.preselectedIndex   - Índice ya seleccionado (selectedOrderIndex).
 */
const TiempoServicioPanel = ({
  show,
  onClose,
  order = [],
  tiempos = [],
  onAssignTiempo,
  preselectedIndex = null,
}) => {
  // Índices de productos seleccionados en este panel
  const [checkedIndices, setCheckedIndices] = useState(() => {
    if (preselectedIndex !== null && preselectedIndex >= 0) {
      return [preselectedIndex];
    }
    return [];
  });

  // Resetear cuando el panel se abre
  React.useEffect(() => {
    if (show) {
      setCheckedIndices(
        preselectedIndex !== null && preselectedIndex >= 0
          ? [preselectedIndex]
          : []
      );
    }
  }, [show, preselectedIndex]);

  if (!show) return null;

  /**
   * Alternar selección de un ítem.
   */
  const toggleIndex = (idx) => {
    setCheckedIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  /**
   * Seleccionar / deseleccionar todos los ítems visibles (no cancelados, qty > 0).
   */
  const toggleAll = () => {
    const activeIndices = order
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => !item.isCancelled && !item.isVoided && item.quantity > 0)
      .map(({ idx }) => idx);

    const allChecked = activeIndices.every((i) => checkedIndices.includes(i));
    setCheckedIndices(allChecked ? [] : activeIndices);
  };

  /**
   * Aplicar un tiempo a los ítems seleccionados.
   */
  const applyTiempo = (tiempoLabel) => {
    if (checkedIndices.length === 0) return;
    onAssignTiempo(tiempoLabel, checkedIndices);
  };

  // Filtrar solo ítems activos para mostrar en la lista
  const activeItems = order
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => !item.isCancelled && !item.isVoided && item.quantity > 0);

  const allChecked =
    activeItems.length > 0 &&
    activeItems.every(({ idx }) => checkedIndices.includes(idx));

  return (
    <div className="tiempo-panel-overlay" onClick={onClose}>
      <div
        className="tiempo-panel-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Encabezado ── */}
        <div className="tiempo-panel-header">
          <h3>⏱ Tiempos de Servicio</h3>
          <button className="tiempo-panel-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* ── Lista de productos ── */}
        <div className="tiempo-panel-body">
          <p className="tiempo-panel-hint">
            Selecciona los productos y luego elige el tiempo.
          </p>

          {activeItems.length === 0 ? (
            <p className="tiempo-panel-empty">No hay productos activos en la orden.</p>
          ) : (
            <>
              {/* Seleccionar todos */}
              <label className="tiempo-item tiempo-item-all">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={toggleAll}
                />
                <span className="tiempo-item-name">Todos los productos</span>
              </label>

              <div className="tiempo-items-list">
                {activeItems.map(({ item, idx }) => {
                  const etiquetaActual = item.tiempo || null;
                  return (
                    <label key={idx} className="tiempo-item">
                      <input
                        type="checkbox"
                        checked={checkedIndices.includes(idx)}
                        onChange={() => toggleIndex(idx)}
                      />
                      <span className="tiempo-item-name">
                        {item.quantity}× {item.name}
                      </span>
                      {etiquetaActual && (
                        <span className="tiempo-item-badge">{etiquetaActual}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* ── Botones de tiempo ── */}
        <div className="tiempo-panel-footer">
          <p className="tiempo-panel-footer-hint">
            {checkedIndices.length === 0
              ? "↑ Selecciona al menos un producto"
              : `${checkedIndices.length} producto(s) seleccionado(s)`}
          </p>
          <div className="tiempo-buttons-grid">
            {tiempos.map((t, i) => (
              <button
                key={i}
                className={`tiempo-btn tiempo-btn-${i + 1}`}
                onClick={() => applyTiempo(t.Tiempo)}
                disabled={checkedIndices.length === 0}
              >
                {t.Tiempo}
              </button>
            ))}
          </div>
          <button className="tiempo-cancel-btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default TiempoServicioPanel;
